/**
 * GRUNDO — játékkonstansok.
 *
 * FONTOS: soha ne írj számot közvetlenül a játéklogikába. Minden hangolható
 * érték ide kerül, és élesben az `appConfig/gameplay` Firestore-dokumentumból
 * felülírható — a launch után ezeket valós adaton hangolni kell.
 *
 * Forrás: docs/04-pontrendszer.md
 */

/* ════════════════════════════════════════════════════════════════
   Szintek — 100 szint, 20 rang × 5 fokozat
   ════════════════════════════════════════════════════════════════ */

const LEVEL_RANKS = [
  'ROOKIE', 'BEGINNER', 'NOVICE', 'SKILLED', 'ADVANCED',
  'PRO', 'SPECIALIST', 'EXPERT', 'VETERAN', 'ACE',
  'ELITE', 'MASTER', 'GRANDMASTER', 'CHAMPION', 'BOSS',
  'APEX', 'TITAN', 'ICON', 'LEGEND', 'GRUNDO',
] as const;

const LEVEL_TIERS = ['I.', 'II.', 'III.', 'IV.', 'V.'] as const;

/**
 * A második szint küszöbe — innen skálázódik minden.
 *
 * MÉRT ÉRTÉK, nem érzés: egy valósághű városi kör 230–560 GP-t ad
 * (3,6 km → 231, 5,3 km → 297, 10 km → 558). Három vegyes aktivitás tehát
 * nagyjából ennyi — vagyis az első szintlépés három aktivitás után jön.
 */
const LEVEL_2_GP = 900;

/**
 * A SZÁZADIK szint küszöbe.
 *
 * A követelmény: aki MINDEN NAP aktív, annak is legalább egy év legyen.
 * A napi maximum a lágy plafon körül van (SOFT_CAP_GP_PER_DAY = 5000): efelé
 * a pont fele értéken számít, tehát a gyakorlati napi tető öt-hatezer GP.
 * 5000 × 365 = 1 825 000 — a küszöb ezért 2 000 000: még a legkitartóbb
 * játékosnak is 400 nap.
 *
 * Viszonyítási pontok egy év napi aktív játék után:
 *   ≈400 GP/nap  (napi egy közepes kör)      → 21. szint  (ADVANCED I.)
 *   ≈1500 GP/nap (napi nagy kör + tartás)    → 46. szint  (ACE I.)
 *   ≈5000 GP/nap (plafonon)                   → 94. szint
 */
const LEVEL_100_GP = 2_000_000;

/**
 * A lépcső alakja: L(n) = LEVEL_2_GP × (n−1)^p.
 *
 * MIÉRT NEM MÉRTANI SOROZAT? Mert az állandó százalékos növekedés a két
 * végpont közé szórva mikroszkopikus lépéseket adna elöl (900 → 959 →
 * 1 022…), amitől az első tíz szint egyetlen aktivitás alatt elrepülne. A
 * hatványfüggvény különbségei folyamatosan nőnek (900-tól ~35 000-ig), de az
 * első lépés mérete értelmes marad.
 */
const LEVEL_EXPONENT = Math.log(LEVEL_100_GP / LEVEL_2_GP) / Math.log(99);

/**
 * Kerekítés a LÉPCSŐ nagyságrendjéhez — nem a küszöbéhez.
 *
 * A felhasználó a lépcsőt látja („még 21 000 GP a következő szintig"), nem a
 * kumulált küszöböt. Ha a küszöböket kerekítenénk, a két szomszédos
 * kerekítési hiba összeadódna a különbségükben, és előfordulhatna, hogy a
 * 24. szint OLCSÓBB, mint a 23. — pontosan ezt mértük ki az első változatnál.
 */
function roundGap(gap: number): number {
  const step = Math.pow(10, Math.max(1, Math.floor(Math.log10(gap)) - 1));
  return Math.round(gap / step) * step;
}

/**
 * A száz küszöb.
 *
 * A lépcsőkből építkezünk, nem a küszöbökből, és két szabályt tartunk be:
 *
 *   1. a lépcső SOHA nem lehet kisebb az előzőnél — különben lenne egy szint,
 *      ami olcsóbb az alatta lévőnél, és ezt a játékos észreveszi;
 *   2. a szintértékek szigorúan nőnek — különben két szint egyszerre
 *      teljesülne, és a haladásjelző nullával osztana.
 *
 * Az UTOLSÓ lépcső nyeli el a kerekítések maradékát, hogy a csúcs pontosan a
 * tervezett kerek számon legyen. Ez a lépcső így is a legnagyobb marad.
 */
export function buildLevelThresholds(): number[] {
  const thresholds = [0];
  let previousGap = 0;

  for (let level = 2; level <= 100; level += 1) {
    const exact =
      LEVEL_2_GP * ((level - 1) ** LEVEL_EXPONENT - (level - 2) ** LEVEL_EXPONENT);
    const gap = Math.max(previousGap, roundGap(exact));
    thresholds.push(thresholds[thresholds.length - 1]! + gap);
    previousGap = gap;
  }

  thresholds[99] = LEVEL_100_GP;
  return thresholds;
}

export function buildLevelNames(): string[] {
  return LEVEL_RANKS.flatMap((rank) => LEVEL_TIERS.map((tier) => `${rank} ${tier}`));
}

export const GAMEPLAY = {
  // ── Rács ────────────────────────────────────────────────────────────────
  /** H3 felbontás. ~18,8 m hosszú átló — a GPS-hiba nagyságrendje.
   *  FIGYELEM: ez visszafordíthatatlan döntés. Élő adaton váltani = migráció. */
  H3_RESOLUTION: 12,
  /** Egy cella NÉVLEGES értéke. Szándékosan fix, hogy egy budapesti és egy
   *  oslói cella ugyanannyit érjen (a valós H3-terület ±néhány %-ot ingadozik). */
  CELL_AREA_M2: 307.09,

  // ── Modifierek (időszakos szorzók) ──────────────────────────────────────
  /**
   * A területi modifierek felbontása. ~5,16 km²/cella — nagyjából egy
   * városrész, ami az „ezen a héten a XI. kerületben 2× GP" akcióhoz a
   * megfelelő szemcsézettség.
   *
   * SZÁNDÉKOSAN DURVÁBB A BLOKKOKNÁL (res 9). Így egy blokk pontosan egy
   * modifier-cellába esik, és a birtokolt terület aránya blokkszinten
   * kiszámolható — cellánként végigmenni egy kétmillió cellás birodalmon
   * naponta nem lenne járható.
   */
  MODIFIER_AREA_RES: 7,
  /**
   * Az egyszerre ható modifierek eredőjének plafonja.
   *
   * Több akció szorzói összeszorzódnak. Ez önmagában rendben van, de amikor a
   * modifiereket később automatika is generálja, egy hibás sorozat
   * korlátlanul felszorozhatná a pontokat — és a GP nem évül, tehát az
   * eredmény maradandó. A plafon ezt tövében vágja el.
   */
  MODIFIER_MAX_FACTOR: 5,
  /**
   * Egy modifier leghosszabb élettartama napokban.
   *
   * A véges élettartam a modifier egész biztonsági alapja. Egy százéves
   * lejárat formailag véges, gyakorlatilag viszont örök — ezért a felső
   * korlát. Hosszabb akciót meg lehet hosszabbítani, de nem lehet elfelejteni.
   */
  MODIFIER_MAX_DAYS: 90,
  /**
   * Egy területi modifier legfeljebb ennyi cellát fedhet le
   * (`MODIFIER_AREA_RES` felbontáson, cellánként ~5,1 km²).
   *
   * 500 cella ~2 570 km²: egy nagyváros és a környéke bőven belefér, egy fél
   * ország nem. Ez nem játékszabály, hanem védelem az elgépelt sugár ellen —
   * a „2 km" helyett beírt „200 km" különben csendben országos akciót csinálna.
   */
  MODIFIER_MAX_AREA_CELLS: 500,

  // ── Rögzítés ────────────────────────────────────────────────────────────
  /** Ez alatt az aktivitás nem is menthető. */
  MIN_DISTANCE_M: 100,
  /** Ennél pontatlanabb GPS-pontokat eldobjuk. */
  MAX_GPS_ACCURACY_M: 30,
  /**
   * ÚJRABEMÉRÉS-KÜSZÖB: eddig a pontosságig hiszünk el egy nagy elmozdulást.
   *
   * ⚠️ MÉRT ESET (tesztelő, 2026-09-14, séta, `c9837182`): hazaérve, liftben
   * felfelé a telefon elveszti a GPS-t, és wifi/cella alapján méri be magát
   * újra. A nyomvonal végén ettől 510 m, 504 m és 249 m ugrás keletkezett,
   * közben egy másodperc alatt 32 méteres lépések — a Trust Score
   * teleport-jele 0%-ot adott, és ~1,3 km hamis táv került a sétába.
   *
   * A KÜLÖNBSÉG A PONTOSSÁGBAN LÁTSZIK, nem a sebességben: a valódi séta
   * végig 3-8 méteres fixekkel ment, az ugrások 13-28 méteresekkel. Mind a 30
   * méteres elfogadási határ ALATT, ezért mentek át a szűrőn.
   *
   * ⚠️ EZ NEM SPORTÁGI HATÁR. A szabály az, hogy egy bizonytalan fix nem
   * állíthat magáról nagyobb elmozdulást, mint a SAJÁT bizonytalansága —
   * ezért nem mond ellent annak a döntésnek, hogy a sebességplafon
   * (`tracking/filter.ts`, 40 m/s) szándékosan nem mozgásforma-függő.
   *
   * ⚠️ ISMERT ÁR: egy valódi, hosszú jelvesztés utáni első fix (alagút,
   * bevásárlóközpont) is eleshet, ha pontatlanul jön. Ilyenkor a nyomvonal a
   * következő, pontos fixnél veszi fel a fonalat.
   */
  RELOCATION_ACCURACY_M: 15,
  /** Két egymást követő pont között ennél több cellát kitölteni gyanús
   *  (~750 m hézag) — a Trust Score jelzi. */
  MAX_GRID_PATH_CELLS: 40,
  /**
   * A GPS-horgony (anchor) sugara méterben — ezen belüli vándorlás sosem
   * számít elmozdulásnak, még akkor sem, ha egyenként minden egyes lépés túl
   * van az egyszerű pontpáronkénti küszöbön (`tracking/filter.ts`
   * `MIN_MOVE_M`).
   *
   * MÉRT ESET (2026-08): telefon zárolt képernyővel egy órán át
   * mozdulatlanul, 2,99 km rögzített futással és 703 m hamis emelkedéssel —
   * a beltéri többutas terjedés miatt egy-egy fix 5-15 métert is ugorhat,
   * ami pontpáronként nézve mindig „elfogadhatónak" tűnik, csak összeadva
   * lesz belőle hamis táv. A `tracking/recorder.ts` (élő rögzítés) és a
   * `game/splits.ts` (utólagos részidő/szintprofil-számítás) EGYARÁNT ezt a
   * sugarat használja, hogy a két számítás ne térjen el egymástól.
   */
  GPS_STATIONARY_RADIUS_M: 12,

  // ── Automatikus szünet (Geri döntése, GRUNDO #72; spec: docs/02) ─────────
  /**
   * Ez alatt az időablakra átlagolt sebesség alatt állónak vesszük (km/h).
   * A `GPS_STATIONARY_RADIUS_M`-en belüli elmozdulás zaj, nem mozgás — enélkül
   * a séta 1 km/h-ja álló helyzetben sosem teljesülne (a zaj 1–3 km/h).
   */
  AUTO_PAUSE_SPEED_KMH: { run: 3, walk: 1, ride: 5 } as const,
  /**
   * Ennyi ideig kell állni a szünethez, típusonként (ms) — Geri döntése,
   * GRUNDO #73: bringa 15 s, futás 30 s, séta 60 s. Mindegyik hosszabb, mint
   * amennyi alatt a küszöbsebesség kiviszi a zajsugárból (8,6 / 14,4 / 43,2 s).
   */
  AUTO_PAUSE_ENTER_MS: { run: 30_000, walk: 60_000, ride: 15_000 } as const,
  /** Ennyi ideig kell a küszöb fölött haladni az automatikus folytatáshoz (ms). */
  AUTO_PAUSE_EXIT_MS: 5_000,
  /** A szünet kezdőpontjától ennyi cellán (H3 res 12) belül számít állásnak. */
  AUTO_PAUSE_RADIUS_CELLS: 2,
  /**
   * Háttérben ilyen ritkán ürítjük a natív sort, és értékeljük a csendet/indulást
   * (ms). Előtérben a másodperces tick és az élő minták döntenek; háttérben a
   * minták nem jutnak el a JS-hez, amíg a sort ki nem ürítjük — a csend-ágat
   * ezért csak kiürítés UTÁN szabad kiértékelni. 5 s: a belépési ablakok
   * legrövidebbje (15 s) alatt legalább háromszor lefut, de nem terheli az
   * akkumulátort.
   */
  AUTO_PAUSE_BACKGROUND_POLL_MS: 5_000,

  // ── Bezárás ─────────────────────────────────────────────────────────────
  /**
   * A bezáráshoz LEGALÁBB EGY kihagyott mező kell a két nyomvonal között.
   *
   * Egy ideig nulla volt, hogy a keskeny kör is számítson — de kiderült, hogy
   * ez kaput nyit: ha ugyanazokon a cellákon jössz vissza, MINDEN cella
   * újralátogatás, és mindegyik saját beágyazott hurkot szül. Egyetlen
   * oda-vissza séta egy 250 méteres utcán így TIZENHÁROM hurkot generált, és
   * a folyosót azonnal 5-ös védelemre vitte. Az egy kihagyott mező
   * megkövetelése ezt a kaszkádot tövében vágja el.
   *
   * ⚠️ VOLT EGY KITÉRŐ 4-RE (2026-08-18), azzal az indoklással, hogy az 1–3
   * mezős önmetszések GPS-remegésből születnek. MEGMÉRTÜK, és nem így van:
   *
   *   - egyenes és oda-vissza séta ±3…±12 m zajjal, 320 futásban: a legnagyobb
   *     belső mezőszám MINDIG 0 — az 1-es küszöb tehát már teljesen kiszűri;
   *   - oda-vissza „folyosó" 10–60 m szélességgel: hurok nem is keletkezik,
   *     mert nincs újralátogatott cella;
   *   - amit a 4 ténylegesen levágott, az VALÓDI kis kör volt: 40 m oldal
   *     (2 belső mező) és 50 m oldal (3 belső mező) — egy belső udvar vagy egy
   *     kisebb háztömb.
   *
   * Ezért állítottuk vissza 1-re. Ha valaki megint emelni akarja, előbb a
   * /admin/aktivitasok auditban nézze meg VALÓDI aktivitásokon, hány hurok esik
   * ki `interior_too_small` okkal — a szintetikus zajmodell nem mutatja a
   * városi kanyonban sodródó GPS-hibát.
   *
   * A gyakorlatban ez ~30 méteres hézagot jelent a két nyomvonal között: egy
   * átlagos utca két járdája NEM elég, egy háztömb megkerülése igen.
   *
   * A GPS-remegésből eredő ál-hurkokat emellett a `MIN_LOOP_STEPS` szűri.
   */
  MIN_INTERIOR_CELLS: 1,
  /**
   * Ennyi cellát kell bejárni két látogatás között, hogy bezárásnak számítson.
   *
   * EZ az ál-hurkok elleni védelem: hat res 12 cella ~110 méternyi út. Álló
   * helyzetben a fix pár méteres körben vándorol, ami ennyi cellát nem érint.
   */
  MIN_LOOP_STEPS: 6,
  /** Védőkorlát a befoglaló doboz cellaszámára — repülő, vonat. A ≈12 km² fölötti
   *  hurkot a compact kitöltés res10-en méri, így a tényleges határ ≈7 500 km²-es
   *  doboz (docs/03 → Védőkorlát). */
  MAX_LOOP_BBOX_CELLS: 500_000,

  // ── Védelem ─────────────────────────────────────────────────────────────
  MAX_DEFENSE: 5,
  /** Szorzó az ÚJ védelmi szint szerint: [1×, 2×, 3×, 4×, 5×] */
  DEFENSE_MULTIPLIER: [1.0, 1.5, 2.0, 3.0, 5.0],

  // ── Pontozás ────────────────────────────────────────────────────────────
  /**
   * A terület GYÖKÉVEL arányos pont — nem magával a területtel.
   *
   * MIÉRT? Mert a bezárt terület a hurok méretének NÉGYZETÉVEL nő, a megtett
   * út viszont csak lineárisan. A korábbi „1 GP / 1000 m²" szabálynál ezért a
   * geometria uralta a játékot: egy 0,8 km-es sétánál az igénypont az alap
   * hatszorosa volt, egy 11 km-es körnél a százhetvenkétszerese. Aki egyetlen
   * nagy kört írt le, nagyságrendekkel többet kapott, mint aki ugyanannyit
   * mozgott kisebb körökben.
   *
   * A gyök a hurok LINEÁRIS méretével arányos, tehát a pont a megtett úttal
   * nő, nem a négyzetével. A nagyobb kör továbbra is többet ér — csak nem
   * aránytalanul.
   *
   * Az érték kalibrálva: egy háztömb körüli séta ~26, egy 5 km-es kör ~150,
   * egy 11 km-es bringakör ~340 igénypont.
   */
  CLAIM_GP_PER_SQRT_KM2: 120,
  /** Alappont — akkor is jár, ha nem zárul be a kör. Ez a rendszer
   *  legfontosabb ösztönző eleme. */
  BASE_GP_PER_KM: { run: 10, walk: 10, ride: 4 } as const,
  /**
   * RÉS: két egymást követő használható pont ennél távolabb — a köztük lévő
   * egyenes szakasz NEM ad alappontot (Geri döntése, GRUNDO #72). Ugyanaz a
   * ~750 m-es hézag, mint a `MAX_GRID_PATH_CELLS` (40 cella × ~18,8 m
   * cellaközép-távolság H3 res 12-n); méterben, mert az élő előnézet minden
   * mintánál újraszámolja, és a cellaátváltás ott drága. A cellaérintés, a
   * kijelzett táv és a statisztika változatlan.
   */
  GP_GAP_MIN_DISTANCE_M: 750,
  /** Idegentől elvett területre. */
  STEAL_BONUS: 0.5,
  /** Védett zóna áttörésekor, amikor a terület még nem cserél gazdát. */
  BREAKTHROUGH_BONUS: 0.25,

  // ── Sorozat ─────────────────────────────────────────────────────────────
  DAILY_STREAK_STEP: 0.05,
  DAILY_STREAK_MAX: 1.5,
  /** Egy kihagyott nap hetente nem töri meg a sorozatot. */
  STREAK_FREEZES_PER_WEEK: 1,
  /** A heti sorozat feltétele: ennyi aktív nap kell egy héten, hogy a hét számítson. */
  WEEK_STREAK_MIN_ACTIVE_DAYS: 3,
  /**
   * A streak-emlékeztető helyi órája (docs/02 → Push-események: „este 18:00
   * helyi idő"). Nem hangolható: az értesítés időzítése, nem játékszabály.
   */
  STREAK_REMINDER_LOCAL_HOUR: 18,
  WEEK_STREAK_MILESTONES: { 4: 500, 12: 2000, 26: 5000, 52: 12000 } as Record<number, number>,

  // ── Napi jóváírás ───────────────────────────────────────────────────────
  /** Tartás-bónusz: 100 GP / km² / nap = a foglalási érték 10 %-a. */
  HOLD_GP_PER_KM2: 100,
  HOLD_GP_DAILY_CAP: 1000,
  /** Az inaktív birodalom nem termel. */
  HOLD_REQUIRES_ACTIVE_DAYS: 7,
  /** Lágy plafon: efölött 50 %-os hozam. Nem kemény korlát. */
  SOFT_CAP_GP_PER_DAY: 5000,
  SOFT_CAP_RATE: 0.5,

  // ── Szintek (kumulált GP) ──────────────────────────────────────
  /**
   * SZÁZ szint: 20 rangnév, mindegyik öt fokozattal (ROOKIE I. … GRUNDO V.).
   *
   * A lépcsőket NEM kézzel írjuk le. Száz szám kézi karbantartása garantáltan
   * eltör: egyetlen elgépelés nem monoton lépcsőt ad, amitől a szint
   * csökkenhetne — és ezt a szemnek kéne észrevennie. A képlet ehelyett
   * ellenőrizhető, hangolható, és a teszt állítást tud tenni a TULAJDONSÁGAIRA
   * (monotonitás, növekvő különbségek, a két végpont).
   */
  LEVELS: buildLevelThresholds(),
  LEVEL_NAMES: buildLevelNames(),
  /** A távolság-létra a jelvények szintjén marad meg (rétegenként). */
  DISTANCE_BADGE_LADDER_KM: [10, 50, 100, 250, 500, 1000, 2500, 5000, 10000, 25000],
  /**
   * A bringás létra a közös tíz fok FÖLÖTT folytatódik (Geri, 2026-11-02):
   * kerékpáron a 25 000 km reálisan elérhető, gyalog nem.
   */
  DISTANCE_BIKE_BADGE_EXTRA_KM: [50000, 100000, 250000, 500000, 1000000],

  // ── Trust Score ─────────────────────────────────────────────────────────
  /**
   * MEGFIGYELŐ MÓD: a pontszám kiszámolódik és elmentődik, de nem blokkol.
   *
   * A heurisztika egyetlen valódi nyomvonalon sem futott még le. Ha élesben
   * döntene, egy félreítélés úgy jelentkezne, hogy „nem kaptam meg a
   * területet" — és semmi nem árulná el, hogy a Trust Score vette el.
   *
   * Ezért előbb mérünk: néhány valódi aktivitás pontszáma megmondja, hol
   * húzódnak a valós határok. Élesítés: ez a kapcsoló `false`-ra.
   *
   * ⚠️ Idegeneket beengedni CSAK élesített módban szabad.
   */
  TRUST_OBSERVE_ONLY: true,
  TRUST_THRESHOLD_ACCEPT: 80,
  TRUST_THRESHOLD_REJECT: 50,
  TRUST_AUTO_APPROVE_MINUTES: 60,
  /*
    FELHASZNÁLÓI BIZALMI SZINT (docs/03 → „Felhasználói bizalmi szint",
    pontosítás: Geri, 2026-10-11, GRUNDO #91). A szint a küszöböket mozgatja.
    `new`: kevesebb mint ennyi TISZTA aktivitás; `established`: legalább ennyi.
  */
  TRUST_LEVEL_ESTABLISHED_CLEAN: 10,
  /** `trusted`: legalább ennyi tiszta aktivitás ÉS soha egy megalapozott bejelentés sem. */
  TRUST_LEVEL_TRUSTED_CLEAN: 100,
  /** `watched`: ennyi napig minden aktivitás legfeljebb `pending_review` lehet. */
  TRUST_WATCHED_DAYS: 30,
  /** A `new` szint szigorúbb küszöbei (az `established` az alap küszöböket használja). */
  TRUST_NEW_THRESHOLD_ACCEPT: 85,
  TRUST_NEW_THRESHOLD_REJECT: 60,
  /** A `trusted` szint enyhébb küszöbei. */
  TRUST_TRUSTED_THRESHOLD_ACCEPT: 70,
  TRUST_TRUSTED_THRESHOLD_REJECT: 40,
  /** A moderációs sor vállalt átfutása (docs/03 → „Admin döntés … 24 órás SLA-val"), órában. */
  TRUST_REVIEW_SLA_HOURS: 24,
  /** A fellebbezés szövegének felső korlátja (karakter). */
  TRUST_APPEAL_MESSAGE_MAX: 500,
  /**
   * EGYIDEJŰSÉG (docs/03 → kiegészítő ellenőrzések, önmagában elutasít): a
   * felhasználó két aktivitása nem fedhet át időben. Ennyi másodperc átfedés
   * még belefér — egy leállított és azonnal újraindított rögzítés órája
   * pár másodpercet csúszhat.
   */
  TRUST_OVERLAP_TOLERANCE_S: 60,
  /*
    TELEPORT-JEL (docs/03, 4. jel — mért javítás, Geri jóváhagyásával, #91).
    Egy UGRÁS (egymást átfedő „lehetetlen” ablakok sora) egyszer számít, és a
    sebességből előbb levonjuk a két végpont jelentett GPS-pontosságát: a
    bemelegedő GPS 14 m-es pontossággal 20–40 m-t ugrál, ami 4 s alatt
    „25–29 km/h” — az éles 50 jelölt aktivitásból 21-nél csak ez volt.
  */
  /** Egy közönséges ugrás levonása a jel 0..1 skáláján. */
  TRUST_TELEPORT_EPISODE_PENALTY: 0.25,
  /** Ha az ugrás (pontossággal csökkentett) sebessége a „lehetetlen” határ ennyiszerese, a jel azonnal 0. */
  TRUST_TELEPORT_SEVERE_FACTOR: 4,
  /**
   * TÖRTÉNETI TEMPÓ (docs/03 → Trust Score, 6. jel: „hirtelen 2× jobb tempó").
   * A saját átlag típusonként, a `trusted` aktivitásokból gördülő átlaggal.
   * Ennyi minta alatt még nincs „saját átlag" — egy bemelegítő séta ne
   * tegyen gyanússá egy első futást.
   */
  TRUST_PACE_MIN_SAMPLES: 3,
  /** A gördülő átlag ablaka: ennyi aktivitás után a régiek súlya elhalványul. */
  TRUST_PACE_WINDOW: 20,
  /** Ennél rövidebb aktivitás tempója zaj — sem nem mér, sem nem tanít. */
  TRUST_PACE_MIN_KM: 0.5,
  /** „Hirtelen 2× jobb tempó" — a spec szorzója. */
  TRUST_PACE_JUMP_FACTOR: 2,
  /**
   * A tempóugrás CSAK IRREÁLIS átlagsebességnél büntet (km/h, mozgásidővel;
   * Geri döntése, GRUNDO #100). Aki eddig dugóban araszolt 4 km/h-val, az
   * szabad úton 25-tel nem gyanús — a 2× ugrás önmagában nem bizonyíték.
   */
  TRUST_PACE_REALISTIC_KMH: { run: 20, walk: 8, ride: 55 } as const,
  /*
    GPS-PONTOSSÁG (docs/03, 3. jel: „irreálisan jó ÉS állandó"). Mért eset
    (#100): egy iPhone rövid bringázáson végig 14,25 m-t jelentett — a régi
    szabály az állandóságot önmagában is −0,6-tal büntette. Geri döntése:
    az állandóság csak jó átlag mellett számít, és mindkét levonás feleződik.
  */
  /** Végig (közel) azonos pontosság levonása a jel 0..1 skáláján… */
  TRUST_PRECISION_CONSTANT_PENALTY: 0.3,
  /** …de csak, ha az átlagos jelentett pontosság ennél jobb (m). */
  TRUST_PRECISION_CONSTANT_MAX_MEAN_M: 5,
  /** A 3 m alatti átlag legnagyobb levonása (0 m-nél). */
  TRUST_PRECISION_TOO_GOOD_PENALTY: 0.2,
  /*
    A TÖRTÉNETI JEL további három eleme (docs/03, 6. jel). A spec a részsúlyokat
    nem adja meg — ezek Geri jóváhagyott értékei (GRUNDO #61). A jel 0..1
    skálán mozog, a súlya 10, tehát egy 0,2-es levonás 2 pontot ér.
  */
  /** Új eszköz: olyan eszköz, amelyről még nem volt tiszta aktivitás. */
  TRUST_NEW_DEVICE_PENALTY: 0.2,
  /** Szokatlan hely: a rajt minden eddigi helytől ennél messzebb (km). */
  TRUST_UNUSUAL_PLACE_KM: 50,
  TRUST_UNUSUAL_PLACE_PENALTY: 0.2,
  /** Friss fiók: ennél fiatalabb (nap). */
  TRUST_NEW_ACCOUNT_DAYS: 7,
  TRUST_NEW_ACCOUNT_PENALTY: 0.1,
  /** Ennyi ismert eszközt / helyet őrzünk meg felhasználónként (a legfrissebbeket). */
  TRUST_KNOWN_DEVICES_MAX: 10,
  TRUST_KNOWN_PLACES_MAX: 20,
  /** Két hely ennél közelebb egynek számít (km) — egy város ne töltse meg a listát. */
  TRUST_PLACE_MERGE_KM: 5,

  /*
    A 7. jel — közösségi bejelentések (Geri döntése, 2026-09-27, GRUNDO #65).
    A spec elve: „a bejelentés nem fegyver" — csak több, egymástól FÜGGETLEN
    és maga is JÓ HÍRŰ bejelentő számít. docs/03 → Trust Score, 7. jel.
  */
  /** Jó hírű bejelentő: legalább ennyi napos fiók. */
  TRUST_REPORTER_MIN_ACCOUNT_DAYS: 7,
  /** Jó hírű bejelentő: legalább ennyi tiszta (`trusted`) aktivitás. */
  TRUST_REPORTER_MIN_CLEAN_ACTIVITIES: 5,
  /** Ennyi elutasított bejelentéstől kezdve számít az előélet… */
  TRUST_REPORTER_DISMISSED_MIN: 3,
  /** …és ha a helytálló bejelentéseinek aránya ez alatt van, elveszti a hitelességét. */
  TRUST_REPORTER_MIN_UPHELD_SHARE: 1 / 3,
  /** Ennyi független, jó hírű bejelentéstől az aktivitás legfeljebb `pending_review` lehet. */
  TRUST_REPORTS_REVIEW_CAP: 3,
  /** Aktivitásonként legfeljebb ennyi bejelentőt vizsgálunk — a költség korlátja. */
  TRUST_REPORTS_MAX_REPORTERS: 20,
  /** Sebességplafonok típusonként (km/h). */
  MAX_SPEED_KMH: { run: 25, walk: 12, ride: 80 } as const,

  // ── Adatvédelem ─────────────────────────────────────────────────────────
  PRIVACY_RADII_M: [50, 100, 200] as const,
  PRIVACY_DEFAULT_RADIUS_M: 200,
  PRIVACY_DEFAULT_ON: true,

  // ── Küldetés-ajánló ─────────────────────────────────────────────────────
  /**
   * A felajánlható időkeretek percben. Időalapú tervezésnél a
   * felhasználónak nem kell fejben átváltania, hogy nála 45 perc hány km.
   */
  MISSION_MINUTE_OPTIONS: [15, 30, 45, 60] as const,
  /** Az egyedi időmegadás határai percben — a szerver ugyanezt ellenőrzi. */
  MISSION_MIN_MINUTES: 5,
  MISSION_MAX_MINUTES: 480,
  /**
   * Ennyi irányban keresünk kört a jelenlegi pozíció körül (docs/02: „8
   * irányban"). Minden irány egy alap Directions-hívás; ha a geometriai
   * ellenőrzés rosszul illesztett köztes pontot talál, ugyanazt az irányt
   * legfeljebb kétszer újjátervezi.
   */
  MISSION_BEARINGS: 8,
  /**
   * Az ideális kör kerülete és a VALÓDI úthálózaton megtett táv aránya.
   *
   * Egy tökéletes kört nem lehet végigfutni: az utcák derékszögben állnak, a
   * folyók és a vasút kerülőt kényszerítenek. A sugarat ezért ennyivel
   * KISEBBRE vesszük — különben minden ajánlat túllőné a kért időt.
   *
   * ⚠️ MÉRT ÉRTÉK, nem becslés (2026-08-22, 3 budapesti kiindulás × 8 irány,
   * éles Directions-válaszokkal, `walking` profil, 7,50 km-es célhossz):
   *
   *   tényező | tényleges átlaghossz | tűrésen belül
   *   --------|----------------------|---------------
   *     1,25  |      8,55 km         |    15/24
   *     1,35  |      7,97 km         |    19/24
   *     1,40  |      7,50 km         |    22/24
   *     1,45  |      7,16 km         |    22/24
   *
   * A korábbi 1,25 tizennégy százalékkal HOSSZABB kört adott a kértnél: aki 45
   * percre kért ajánlatot, 51 percnyit kapott — és a jelöltek több mint
   * harmada emiatt esett ki a tűréshatáron.
   *
   * A tényleges hosszt továbbra is a Directions válasza adja meg, és a
   * `MISSION_DISTANCE_TOLERANCE` szűri ki, ami így is mellément.
   */
  MISSION_DETOUR_FACTOR: 1.4,
  /** A célhossztól ennyivel térhet el egy ajánlat (docs/02: „±15 %"). */
  MISSION_DISTANCE_TOLERANCE: 0.15,
  /**
   * Hány pontot adunk át a útvonaltervezőnek egy körhöz.
   *
   * Kevesebb pont: a tervező „lerövidíti" a kört, és inkább oda-vissza megy
   * ugyanazon az úton — abból nincs bezárás. Több pont: minden kanyar
   * kötöttebb, és a tervező nem tud valódi úthoz igazodni. Öt köztes pont
   * elég ahhoz, hogy a kör körbeérjen, és elég laza, hogy utcákat kövessen.
   *
   * ⚠️ AZ ÖTÖT MEGMÉRTEM, NE VIDD LEJJEBB (2026-08-22, 24 jelölt/változat).
   * Három ponttal az útvonalon tényleg kevesebb a fölösleges kitérő — de a
   * bezárt terület 2,015 km²-ről 1,147-re esik, mert egy háromszög-alakú kör
   * egyszerűen kevesebbet fog közre. A hatos és a nyolcas se hozott többet.
   */
  MISSION_WAYPOINTS: 5,
  /**
   * Alapértelmezett tempó (másodperc/km) típusonként, ha a felhasználónak
   * még nincs elég mért aktivitása.
   *
   * Szándékosan óvatos (lassabb) értékek: aki az elsőt kapja, inkább érezze
   * könnyűnek a küldetést, mint hogy ne érjen vissza a vállalt időben.
   */
  MISSION_DEFAULT_PACE_S_PER_KM: { run: 360, walk: 780, ride: 165 } as const,
  /** Ennyi korábbi aktivitásból számolunk átlagtempót. */
  MISSION_PACE_SAMPLE_ACTIVITIES: 10,
  /**
   * Hány ajánlatot kérünk alapból, és mennyi állítható be a részletes
   * keresőben (Geri kérése, 2026-08-29).
   *
   * A szám FELSŐ KORLÁT, nem garancia: a `missions.ts` `pickMissions` a
   * szigorú menetben karakterenként egy ajánlatot ad, és ha így kevesebb jön
   * ki a kértnél, fokozatosan lazít a hasonlóság-szűrésen, hogy elérje a
   * számot — ugyanazt az útvonalat viszont soha nem adja vissza kétszer.
   *
   * ⚠️ Nagyon hosszú (100 km fölötti) körnél a gyakorlati plafon ettől
   * függetlenül alacsonyabb, mert a szerver `shapedCandidateLimit`-je
   * teljesítményi okból csak 2-4 jelöltre futtatja a drága geometriát.
   */
  MISSION_RESULT_DEFAULT: 3,
  MISSION_RESULT_MIN: 1,
  MISSION_RESULT_MAX: 5,

  /**
   * A→B ODA-VISSZA TERVEZÉS: a kerülő mérete, méterben.
   *
   * Ez az az oldalirányú (a közvetlen A–B vonalra MERŐLEGES) eltérés, amivel
   * az odaút az egyik, a visszaút a másik irányba kitér. A kettő együtt zárja
   * körbe a megszerezhető területet.
   *
   * ⚠️ EZ JÁTÉKKONSTANS, NEM TERVEZŐ-HANGOLÁS. A kerülő mérete közvetlenül a
   * bezárt terület mérete, tehát a „nagy kerülő" gyakorlatilag terület-szerző
   * eszköz. Geri döntése (2026-09-12), lásd
   * `docs/02-funkcionalis-spec.md` → Útvonaltervezés a rögzítés előtt.
   *
   * ⚠️ AZ ÉRTÉK KÖZTES PONTKÉNT HAT, nem a súlyozott sáv szélességeként.
   * Mérve (2026-09-12): a jutalmazott terület szélesítése NEM növeli a
   * kerülőt — a router a megengedett sávon belül is a legolcsóbbat választja,
   * és a „közepes" meg a „nagy" sáv ugyanazt az útvonalat adta. A méretet
   * ezért merőleges köztes pont állítja. Lásd
   * `docs/routing/point-to-point.md` → Mérés.
   */
  ROUTE_DETOUR_OFFSET_M: { small: 500, medium: 1_000, large: 2_000 } as const,

  /**
   * Start and destination closer than this are the same point: a round trip
   * (Geri, #86 — not an error). It is planned through the stops; without a
   * stop there is nothing to loop around yet.
   */
  ROUTE_SAME_POINT_M: 100,

  // ── „Grout” asszisztens: homályos cél a közelben (#86) — nem játékszabály ──
  /**
   * Route length ÷ straight distance to the destination, measured with the real
   * planner (`server/src/scripts/measureAssistantLoop.ts`, 2026-10-09: 4 Budapest
   * origins × 3 distances × 4 directions, 48 routes each, medium detour). The
   * p90 keeps a „max 5 km” request under 5 km for 9 routes out of 10; the median
   * gives the „about X km” estimate in the reply.
   */
  ASSISTANT_ROUTE_RATIO_P90: {
    loop: { walking: 5.7, cycling: 7.7 },
    direct: { walking: 1.85, cycling: 3.0 },
  } as const,
  ASSISTANT_ROUTE_RATIO_MEDIAN: {
    loop: { walking: 4.3, cycling: 4.65 },
    direct: { walking: 1.41, cycling: 1.61 },
  } as const,
  /** How far to look for a vague destination when no distance was said (straight line). */
  ASSISTANT_TARGET_RADIUS_M: { walking: 3_000, cycling: 8_000 } as const,

  // ── Ingyenes korlátok (a Pro ezeket oldja fel — játékbeli előnyt SOHA) ──
  FREE_ROUTE_GENERATIONS_PER_WEEK: 5,
  /**
   * A „Grout” asszisztens (szöveges/hangos útvonalkérés értelmezése) napi kerete
   * felhasználónként — MINDENKIRE vonatkozik, a Pro-ra és az adminra is: ez nem
   * játékbeli előny, hanem visszaélés elleni plafon (Geri, #71: költségplafon
   * nincs, de napi 50 kérés/fő). #79: 100 — a beszélgetés több üzenet (Geri). Egy
   * üzenet egy kérés, akár a gyors mezőből, akár a chatből jön.
   * Spec: `docs/routing/route-intelligence-plan.md`.
   */
  ASSISTANT_REQUESTS_PER_DAY: 100,
  FREE_ACTIVE_EQUIPMENT: 3,
  /**
   * CONNECTOR — external apps and devices (DECISIONS #118, `docs/connector/`).
   * A PRO feature that is free for now (Geri): `false` lets everyone use the
   * activity export and the external connections. Switching it to `true` in the
   * admin game config gates them behind PRO without a code change — the check
   * lives in one place, `server/src/lib/entitlements.ts`.
   */
  CONNECTOR_REQUIRES_PRO: false,
  /**
   * Import window (Geri, DECISIONS #118): an activity is imported only if at
   * most this many hours passed between its end and its arrival on the server,
   * and never if it started before the account was created.
   */
  IMPORT_MAX_AGE_HOURS: 72,
  /**
   * How long an imported activity waits after its end before it is saved
   * (#118 priority: the GRUNDO recording of the same movement wins). Geri,
   * 2026-10-25: 6 hours, so a GRUNDO upload delayed by a weak signal or a
   * closed app still lands first.
   */
  CONNECTOR_IMPORT_GRACE_MINUTES: 360,
  FREE_VISIBLE_HISTORY: 30,
  EMAIL_VERIFICATION_GRACE_DAYS: 7,

  // ── Fióktörlés (docs/05 → Fióktörlés folyamata; szerkezeti, nem hangolható) ──
  /** A törlési kérelem után ennyi napig visszavonható, utána végleges. */
  ACCOUNT_DELETION_GRACE_DAYS: 30,
  /** A kérelemhez legfeljebb ennyi másodperce történt belépés kell (`auth_time`). */
  ACCOUNT_DELETION_REAUTH_MAX_AGE_S: 300,
  /** Adatexport (docs/05 → Adatmegőrzés): ennyi óránként kérhető egy… */
  DATA_EXPORT_COOLDOWN_HOURS: 24,
  /** …és a letöltési link ennyi napig él (a V4 aláírt URL felső határa is 7). */
  DATA_EXPORT_LINK_DAYS: 7,

  // ── Korhatár (docs/06 → 460. sor; jogi, nem hangolható) ──
  /** Ennél fiatalabb nem regisztrálhat — a profil létrehozásakor kikényszerítve. */
  MIN_AGE_YEARS: 16,

  // ── Privát üzenetek (docs/05 → „Privát üzenetek"; Geri, #68) ──
  DM_MAX_TEXT_LENGTH: 2_000,
  /** Új szál indítása naponta — kölcsönös követőnek nem számít bele. */
  DM_NEW_CONVERSATIONS_PER_DAY: 10,
  /** Ennyi üzenet mehet egy szálba, amíg a címzett nem fogadta el / nem válaszolt. */
  DM_UNACCEPTED_MESSAGE_CAP: 3,
  /** Általános fék mindenkire, a kölcsönös követőkre is. */
  DM_MESSAGES_PER_MINUTE: 30,
  /** A kliensoldali tömörítés UTÁN; a `storage.rules` is ezt kényszeríti ki. */
  DM_IMAGE_MAX_BYTES: 5 * 1024 * 1024,
  DM_IMAGE_RETENTION_HOURS: 24,
  /** A válasz- és listaelőnézet hossza. */
  DM_PREVIEW_LENGTH: 120,
  /**
   * Szerkesztés/törlés előtti eredeti szöveg megőrzése, CSAK adminnak (Geri,
   * 2026-11-04): ennyi nap után a `message-images` job törli. Szerkeszteni és
   * törölni időkorlát nélkül lehet.
   */
  DM_REVISION_RETENTION_DAYS: 30,
  /** Ennyi ideig „online" a felhasználó az utolsó előtéri szívverés után. */
  PRESENCE_ONLINE_WINDOW_MS: 120_000,

  // ── Banda chat (docs/05 → „Banda chat"; Geri, #70–#71) ──
  /** = a régi üzenőfal hossza (Geri, #71). */
  BANDA_CHAT_MAX_TEXT_LENGTH: 1_000,
  /** Az élő ablak és a visszalapozás lapmérete. */
  BANDA_CHAT_PAGE: 50,
  BANDA_CHAT_MESSAGES_PER_MINUTE: 30,
  /**
   * Sorozat-fék (Geri, #71): aki `windowMs` alatt `maxMessages`-nél TÖBBET
   * küldene, az a sorozat következő üzenetét már nem küldheti el, és
   * `cooldownMs`-ig nem írhat. Több szabály egyszerre sértve → a hosszabb
   * várakozás érvényes. A percenkénti fék emellett marad.
   */
  BANDA_CHAT_BURST_RULES: [
    { windowMs: 1_000, maxMessages: 3, cooldownMs: 5_000 },
    { windowMs: 5_000, maxMessages: 5, cooldownMs: 10_000 },
  ],
  /** A válasz-előnézet hossza. */
  BANDA_CHAT_PREVIEW_LENGTH: 120,
  /** Az „új chat-üzenet" push csak ennyi perc csend után megy bandánként — nem üzenetenként. */
  BANDA_CHAT_NOTIFY_QUIET_MINUTES: 15,
  /** Az időre szóló némítások (Geri, #71: 1 óra, 24 óra); a harmadik lehetőség a feloldásig tartó. */
  BANDA_CHAT_MUTE_HOURS: { '1h': 1, '24h': 24 },
  /** Üzenetenként ennyi embert értesít az @említés és a válasz együtt. */
  BANDA_CHAT_MENTIONS_PER_MESSAGE: 5,
} as const;

export type ActivityType = keyof typeof GAMEPLAY.BASE_GP_PER_KM;

/**
 * A `GAMEPLAY` típusa KITÁGÍTVA — ez a futásidejű konfiguráció típusa.
 *
 * A `GAMEPLAY` `as const`, tehát minden mezője literál típus: a
 * `CLAIM_GP_PER_SQRT_KM2` típusa nem `number`, hanem `120`. Ez az
 * alapértékekhez helyes, a felülírt konfigurációhoz viszont hazugság lenne — és
 * nem is ártalmatlan: a `TRUST_OBSERVE_ONLY` típusa `true` volna, amitől a
 * TypeScript a kikapcsolt ághoz tartozó kódot elérhetetlennek hinné.
 *
 * A `Widen` ezért literálból alaptípust csinál és leveszi a `readonly`-t. Így a
 * típus továbbra is a `GAMEPLAY`-ből származik — egy új konstans automatikusan
 * megjelenik benne, nincs kézzel karbantartott párhuzamos interfész.
 */
type Widen<T> = T extends readonly (infer U)[]
  ? Widen<U>[]
  : T extends number
    ? number
    : T extends string
      ? string
      : T extends boolean
        ? boolean
        : { -readonly [K in keyof T]: Widen<T[K]> };

export type GameplayConfig = Widen<typeof GAMEPLAY>;

/**
 * Az alapértékek, a futásidejű konfiguráció alakjában.
 *
 * Ezt használják a `src/game/` tiszta függvényei alapértelmezett paraméterként,
 * hogy a meglévő hívási helyek változatlanul működjenek. A szerver ehelyett az
 * `appConfig/gameplay`-ből feloldott pillanatképet adja át.
 */
export const DEFAULT_GAMEPLAY: GameplayConfig = GAMEPLAY as unknown as GameplayConfig;

/** Standalone REFHEX rules; no GP, territory or server-tunable effects. */
export const REFHEX = {
  unlockHoldMs: 3_000,
  countdownMs: 3_000,
  durationMs: 10_000,
  missPenaltyMs: 1_000,
  slowDelayMinMs: 1_000,
  slowDelayMaxMs: 3_000,
  columns: 6,
  rows: 6,
  feedbackHoldMs: 180,
  feedbackExitMs: 300,
  leaderboardSize: 10,
  dailyRewardGp: 50,
} as const;
